/**
 * Pure presentational helpers for /scout — no JSX, no I/O. Shape the S1–S3.5
 * data objects into display-ready strings/styles. Kept separate so the render
 * layer stays thin.
 */

import { theme } from "../../data/theme.js";

/** "8 dynasty · 2 keeper · 1 redraft · 1 best-ball" from a manager's sampled set. */
export function leagueLandscape(sampled) {
  let dyn = 0, keep = 0, re = 0, bb = 0, other = 0;
  for (const s of sampled || []) {
    if (s.bestBall) { bb++; continue; }
    if (s.leagueType === 2) dyn++;
    else if (s.leagueType === 1) keep++;
    else if (s.leagueType === 0) re++;
    else other++;
  }
  const parts = [];
  if (dyn) parts.push(`${dyn} dynasty`);
  if (keep) parts.push(`${keep} keeper`);
  if (re) parts.push(`${re} redraft`);
  if (bb) parts.push(`${bb} best-ball`);
  if (other) parts.push(`${other} other`);
  return parts.join(" · ") || "no format-similar leagues";
}

/** Reachability gate → label + color (the "should I even bother" signal). */
export const REACH_STYLE = {
  active:  { label: "Active",  color: theme.colors.status.success, hint: "moved recently — reachable" },
  cooling: { label: "Cooling", color: theme.colors.status.warning, hint: "slowing down" },
  dormant: { label: "Dormant", color: theme.colors.status.danger,  hint: "hard to reach right now" },
};

export const CONFIDENCE_STYLE = {
  high:    { label: "High confidence", color: theme.colors.status.success },
  medium:  { label: "Medium confidence", color: theme.colors.status.warning },
  limited: { label: "Limited data", color: theme.colors.status.danger },
};

const FAAB_HINT = { hoarder: "rarely bids", balanced: "spends situationally", aggressive: "spends big", unknown: "no FAAB data" };
const ACTIVITY_HINT = { low: "light", moderate: "occasional", high: "active", hyperactive: "hyperactive" };

/** Leverage believers = rate × value-percentile (the "what he'll overpay for"). */
export function topLeverage(believers, n = 3) {
  return [...(believers || [])]
    .sort((a, b) => (b.leverageScore - a.leverageScore) || ((b.value ?? 0) - (a.value ?? 0)))
    .slice(0, n);
}

/** Pet players = raw roster-rate (cheap guys he hoards everywhere). */
export function topPets(believers, n = 8) {
  return [...(believers || [])]
    .sort((a, b) => (b.rate - a.rate) || ((b.value ?? 0) - (a.value ?? 0)))
    .slice(0, n);
}

/** Build the six-axis readout rows for the deep-dive profile (no charts). */
export function axisRows(dd) {
  const { tradeStyle: ts = {}, faab: f = {}, rebuild: rb = {}, positional: pos = {} } = dd;
  const rows = [];

  rows.push({
    key: "faab", label: "FAAB posture",
    value: f.spentPct == null ? "—" : `${f.spentPct}%`,
    band: f.band, hint: FAAB_HINT[f.band] || "",
  });
  rows.push({
    key: "activity", label: "Trade activity",
    value: `${ts.tradeCount ?? 0} trades`,
    band: ts.activityBand, hint: ACTIVITY_HINT[ts.activityBand] || "",
  });
  rows.push({
    key: "initiation", label: "Initiation",
    value: ts.lowConfidence || ts.initiationRate == null ? "thin sample" : `${Math.round(ts.initiationRate * 100)}%`,
    band: ts.initiationBand, hint: ts.lowConfidence ? `only ${ts.tradeCount} trades — not graded` : (ts.initiationBand || ""),
    fragile: ts.lowConfidence,
  });
  rows.push({
    key: "rebuild", label: "Win-now ↔ rebuild",
    value: rb.label || "—",
    band: rb.label,
    hint: rb.note ? rb.note : `${rb.confidence || ""} · net picks ${rb.netFuturePicks ?? "—"}, age-tilt ${rb.youthTilt ?? "—"}`,
    fragile: rb.confidence === "low" || !!rb.note,
  });
  rows.push({
    key: "positional", label: "Positional lean",
    value: pos.flags && pos.flags.length ? pos.flags.join(", ") : "balanced",
    hint: pos.rosterMix ? `RB ${pos.rosterMix.rbShare}% / WR ${pos.rosterMix.wrShare}% / TE ${pos.rosterMix.teShare}%` : "",
  });
  return rows;
}

// ─── Raw drill-down log builders (info-first; NO interpretation) ───────────

const SKILL_OK = new Set(["QB", "RB", "WR", "TE"]);

function playerInfo(pid, playersMap, valueMap) {
  const meta = playersMap?.[pid];
  const v = valueMap?.get?.(String(pid));
  const name = meta
    ? (`${meta.first_name || ""} ${meta.last_name || ""}`.trim() || meta.full_name || pid)
    : (v?.name || pid);
  return { player_id: pid, name, position: meta?.position || v?.position || null, value: v?.value ?? null };
}

const pickLabel = (p) => `${p.season ?? "?"} R${p.round ?? "?"}`;

/** One trade from a member's side (roster_id = rid): what they gave vs got. */
export function describeTradeForMember(tx, rid, playersMap, valueMap) {
  const adds = tx.adds || {};
  const drops = tx.drops || {};
  const picks = Array.isArray(tx.draft_picks) ? tx.draft_picks : [];
  const got = Object.keys(adds).filter((pid) => adds[pid] === rid).map((pid) => playerInfo(pid, playersMap, valueMap));
  const gave = Object.keys(drops).filter((pid) => drops[pid] === rid).map((pid) => playerInfo(pid, playersMap, valueMap));
  const gotPicks = picks.filter((p) => p.owner_id === rid && p.previous_owner_id !== rid).map(pickLabel);
  const gavePicks = picks.filter((p) => p.previous_owner_id === rid && p.owner_id !== rid).map(pickLabel);
  const faab = (Array.isArray(tx.waiver_budget) ? tx.waiver_budget : [])
    .filter((w) => w.sender === rid || w.receiver === rid)
    .map((w) => ({ amount: w.amount, dir: w.receiver === rid ? "in" : "out" }));
  return { gave, got, gavePicks, gotPicks, faab };
}

/** Chronological raw trade log across the member's tx leagues (newest first). */
export function buildTradeLog(txLeagues, playersMap, valueMap) {
  const rows = [];
  for (const lg of txLeagues || []) {
    const rid = lg.roster_id;
    for (const t of lg.completeTx || []) {
      if (t.type !== "trade") continue;
      if (!Array.isArray(t.roster_ids) || !t.roster_ids.includes(rid)) continue;
      rows.push({
        created: t.created || 0,
        league_name: lg.league_name, season: lg.season,
        ...describeTradeForMember(t, rid, playersMap, valueMap),
      });
    }
  }
  return rows.sort((a, b) => b.created - a.created);
}

/** Chronological raw waiver/FAAB log (winning waivers only; newest first). */
export function buildWaiverLog(txLeagues, playersMap) {
  const rows = [];
  for (const lg of txLeagues || []) {
    const rid = lg.roster_id;
    for (const t of lg.completeTx || []) {
      if (t.type !== "waiver") continue;
      const adds = t.adds || {};
      const wonPids = Object.keys(adds).filter((pid) => adds[pid] === rid);
      if (!wonPids.length) continue;
      for (const pid of wonPids) {
        const meta = playersMap?.[pid];
        rows.push({
          created: t.created || 0,
          league_name: lg.league_name, season: lg.season,
          player: meta ? (`${meta.first_name || ""} ${meta.last_name || ""}`.trim() || pid) : pid,
          position: meta?.position || null,
          bid: (t.settings || {}).waiver_bid ?? 0,
          budget: lg.faabBudget || 0,
        });
      }
    }
  }
  return rows.sort((a, b) => b.created - a.created);
}

/** ms → "Sep 14, 2025" (UI-only; safe in browser). */
export function fmtDate(ms) {
  if (!ms) return "—";
  try { return new Date(ms).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

export { SKILL_OK };

/** Band → accent color for axis chips. */
export function bandColor(band) {
  const c = theme.colors.status;
  switch (band) {
    case "aggressive": case "hyperactive": case "high": case "proactive": return c.success;
    case "balanced": case "moderate": return c.info;
    case "hoarder": case "low": case "reactive": case "dormant": return c.warning;
    case "rebuild": return c.info;
    case "win-now": return theme.colors.brand.orange;
    case "transitional": return c.warning;
    default: return theme.colors.text.muted;
  }
}
